import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { StateView } from '@/card/record'
import type { Locale } from '@/i18n'
import { ApiError } from '@/lib/api'
import { formatDate, formatNumber } from '@/lib/format'
import { AdminPage, AdminState, Notice, PrimaryButton, SkeletonTable, isForbidden } from '@/admin/AdminShell'
import {
  MODEL_KEY,
  RESOURCE,
  fetchLeadModel,
  trainLeadModel,
  type Coefficient,
  type LeadModelReport,
  type Scores,
} from './leadScoreApi'

type Translate = (key: string, params?: Record<string, string | number>) => string

/**
 * Качество модели оценки лидов (#129).
 *
 * - **Модель рядом с тем, на чём компания работает без неё.** AUC модели сравнивается с AUC
 *   правила баллов (его ранжирование), log-loss и Brier — с «всегда средней конверсией»: у
 *   правила баллы, а не вероятность, и его log-loss был бы выдуман.
 * - **Отложенная выборка — последние по дате поступления лиды**, а не случайные.
 * - **Откуда признаки обучения**: снимок при приёме, журнал создания или текущая строка лида.
 *   Последнее может описывать квалификацию, а не заявку (ИНН дописали при разговоре), и экран это
 *   число не прячет.
 * - **Текст учтён моделью или правилом** — сказано словами: пока лидов с разобранным текстом и
 *   исходом нет, модель о тексте ничего не знает.
 * - **Мало данных — словами**: лиды оцениваются правилом, без вероятности.
 */
export function LeadScoreModelPage() {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const report = useQuery({ queryKey: MODEL_KEY, queryFn: ({ signal }) => fetchLeadModel(signal), retry: false })
  const train = useMutation({
    mutationFn: () => trainLeadModel(),
    onSuccess: (data) => {
      queryClient.setQueryData(MODEL_KEY, data)
      // Open leads are rescored by the training: their cards show the previous score until reread.
      void queryClient.invalidateQueries({ queryKey: ['lead-score'] })
    },
  })
  const mayTrain = can(user, RESOURCE, 'update')

  return (
    <AdminPage
      sectionKey="nav.section.sales"
      titleKey="nav.leadScoreModel"
      leadKey="leadScore.model.lead"
      forbidden={isForbidden(report.error)}
      primary={
        mayTrain && (
          <PrimaryButton
            icon="refresh"
            label={train.isPending ? t('forecast.model.training') : t('forecast.model.train')}
            onClick={() => train.mutate()}
          />
        )
      }
    >
      <Notice text={train.error ? (train.error instanceof ApiError ? train.error.message : String(train.error)) : null} />
      <AdminState
        isLoading={report.isLoading}
        error={report.error}
        onRetry={() => void report.refetch()}
        forbiddenTitleKey="leadScore.model.forbidden"
        skeleton={<SkeletonTable columns={3} rows={4} />}
      >
        {report.data && <Report report={report.data} t={t} locale={locale} />}
      </AdminState>
    </AdminPage>
  )
}

function Report({ report, t, locale }: { report: LeadModelReport; t: Translate; locale: Locale }) {
  const scored = (
    <p className="t-sm secondary" data-testid="lead-score-coverage">
      {t('leadScore.model.coverage', {
        model: formatNumber(report.scoredLeads.model ?? 0, locale),
        rule: formatNumber(report.scoredLeads.rule ?? 0, locale),
        unscored: formatNumber(report.scoredLeads.unscored ?? 0, locale),
      })}
    </p>
  )
  if (report.status === 'none') {
    return (
      <div className="stack" style={{ gap: 12 }}>
        <StateView boxed kind="empty" title={t('leadScore.model.none.title')}
                   description={t('leadScore.model.none.text', { min: report.configuredMinClosedLeads })} />
        {scored}
      </div>
    )
  }
  if (report.status === 'insufficient_data' || report.metrics == null) {
    return (
      <div className="stack" style={{ gap: 12 }}>
        <StateView boxed kind="empty" title={t('leadScore.model.insufficient.title')}
                   description={t('leadScore.model.insufficient.text', {
                     message: report.message ?? '',
                     date: report.trainedAt ? formatDate(report.trainedAt.slice(0, 10), locale) : '—',
                   })} />
        {scored}
      </div>
    )
  }
  const metrics = report.metrics
  const origins = metrics.origins ?? {}
  return (
    <div className="stack" style={{ gap: 16 }}>
      <p data-testid="lead-score-model-summary">
        {t('leadScore.model.summary', {
          date: report.trainedAt ? formatDate(report.trainedAt.slice(0, 10), locale) : '—',
          train: formatNumber(report.trainLeads ?? 0, locale),
          holdout: formatNumber(report.holdoutLeads ?? 0, locale),
          from: report.holdoutFrom ? formatDate(report.holdoutFrom.slice(0, 10), locale) : '—',
          hot: `${Math.round(report.hotThreshold * 100)} %`,
          warm: `${Math.round(report.warmThreshold * 100)} %`,
        })}
      </p>
      {scored}
      <p className="t-sm" data-testid="lead-score-text-learned">
        {t(report.textLearned ? 'leadScore.model.textLearned' : 'leadScore.model.textByRule')}
      </p>
      {(origins.excluded ?? 0) > 0 && (
        <div className="alert alert-neutral" role="status" data-testid="lead-score-origins">
          <span>{t('leadScore.model.origins', {
            intake: origins.intake ?? 0,
            journal: origins.journal ?? 0,
            excluded: origins.excluded ?? 0,
          })}</span>
        </div>
      )}

      <section className="stack" style={{ gap: 8 }} aria-labelledby="lsm-quality-h">
        <h2 id="lsm-quality-h" className="h2">{t('forecast.model.quality')}</h2>
        <div className="tbl-wrap">
          <table className="tbl" aria-label={t('forecast.model.quality')}>
            <thead>
              <tr>
                <th>{t('forecast.model.col.metric')}</th>
                <th className="num">{t('forecast.model.col.model')}</th>
                <th className="num">{t('leadScore.model.col.baseline')}</th>
              </tr>
            </thead>
            <tbody>
              <MetricRow name={t('forecast.model.auc')} hint={t('leadScore.model.aucHint')}
                         model={metrics.model.auc} baseline={metrics.ruleAuc} t={t} />
              <MetricRow name={t('forecast.model.logLoss')} hint={t('forecast.model.lowerBetter')}
                         model={metrics.model.logLoss} baseline={metrics.baseline.logLoss} t={t} />
              <MetricRow name={t('forecast.model.brier')} hint={t('forecast.model.lowerBetter')}
                         model={metrics.model.brier} baseline={metrics.baseline.brier} t={t} />
            </tbody>
          </table>
        </div>
        <p className="t-sm secondary">{t('leadScore.model.baselineNote')}</p>
      </section>

      <section className="stack" style={{ gap: 8 }} aria-labelledby="lsm-calibration-h">
        <h2 id="lsm-calibration-h" className="h2">{t('forecast.model.calibration')}</h2>
        <div className="tbl-wrap">
          <table className="tbl" aria-label={t('forecast.model.calibration')}>
            <thead>
              <tr>
                <th>{t('forecast.model.col.bucket')}</th>
                <th className="num">{t('leadScore.model.col.leads')}</th>
                <th className="num">{t('forecast.model.col.predicted')}</th>
                <th className="num">{t('leadScore.model.col.actual')}</th>
              </tr>
            </thead>
            <tbody>
              {metrics.calibration.map((bucket) => (
                <tr key={bucket.from}>
                  <td>{`${percent(bucket.from)}–${percent(bucket.to)}`}</td>
                  <td className="num">{formatNumber(bucket.count, locale)}</td>
                  <td className="num">{bucket.meanPredicted == null ? '—' : percent(bucket.meanPredicted)}</td>
                  <td className="num">{bucket.actualRate == null ? '—' : percent(bucket.actualRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="t-sm secondary">
          {t('leadScore.model.rates', {
            train: percent(metrics.trainConversionRate),
            holdout: percent(metrics.holdoutConversionRate),
          })}
        </p>
      </section>

      <section className="stack" style={{ gap: 8 }} aria-labelledby="lsm-coefficients-h">
        <h2 id="lsm-coefficients-h" className="h2">{t('forecast.model.coefficients')}</h2>
        <div className="tbl-wrap">
          <table className="tbl" aria-label={t('forecast.model.coefficients')}>
            <thead>
              <tr>
                <th>{t('forecast.model.col.feature')}</th>
                <th className="num">{t('forecast.model.col.weight')}</th>
                <th>{t('forecast.model.col.direction')}</th>
              </tr>
            </thead>
            <tbody>
              {report.coefficients.map((coefficient) => (
                <tr key={coefficient.code}>
                  <td>{featureName(t, coefficient)}</td>
                  <td className="num">{coefficient.weight.toFixed(2)}</td>
                  <td>{t(coefficient.weight >= 0 ? 'forecast.effect.up' : 'forecast.effect.down')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function MetricRow({ name, hint, model, baseline, t }: {
  name: string
  hint: string
  model: Scores['auc']
  baseline: Scores['auc']
  t: Translate
}) {
  const show = (value: number | null | undefined) => (value == null ? t('forecast.model.undefined') : value.toFixed(3))
  return (
    <tr>
      <td title={hint}>{name}</td>
      <td className="num strong">{show(model)}</td>
      <td className="num">{show(baseline)}</td>
    </tr>
  )
}

function percent(value: number): string {
  return `${Math.round(value * 100)} %`
}

/** Название признака; индикатор источника — «Источник: Сайт». */
function featureName(t: Translate, coefficient: Coefficient): string {
  const [group, value] = coefficient.code.split(':')
  if (value !== undefined) {
    return t('leadScore.feature.source', { value: t(`value.sales.lead.source.${value}`) })
  }
  return t(`leadScore.feature.${group}`)
}
